import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import PageSizeDialog from '../components/ui/PageSizeDialog';
import { eventsApi } from '../api/api';
import { toLocalDate, isUpcomingOrLive } from '../utils/events';

const EVENT_PAGE_SIZE_OPTIONS = [6, 12, 18, 24];

// eventsData is now loaded from the database via PHP API.
// The static array below is kept only as a reference/fallback comment.
// Shape: { id, title, startDate, endDate, time, location, description, imageUrl, color, type }

const _STATIC_EVENTS_PLACEHOLDER = [
  {
    id: 1,
    startDate: '2025-06-21',
    endDate: '2025-06-28',
    title: 'Ram Katha & Divine Blessing',
    location: 'Shree Ram Ranuja Dham, Sacred City, India',
    description: 'Join us for a divine 7-day Ram Katha session.',
    imageUrl: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'orange',
    type: 'katha'
  },
  {
    id: 2,
    startDate: '2025-06-25',
    endDate: '2025-06-25',
    time: '6:00 PM - 9:00 PM',
    title: 'Bhajan Sandhya & Aarti',
    location: 'Main Hall, Shree Ram Ranuja Dham, Sacred City',
    description: 'An evening filled with devotional bhajans and sacred aarti. Join fellow devotees in singing praises of the Divine and experience the bliss of collective worship.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'green',
    type: 'event'
  },
  {
    id: 3,
    startDate: '2025-06-30',
    endDate: '2025-06-30',
    time: '7:00 AM - 12:00 PM',
    title: 'Hanuman Chalisa Path',
    location: 'Hanuman Temple, Sacred City',
    description: 'Special recitation of Hanuman Chalisa with detailed explanation of each verse. Learn the deeper meanings and spiritual significance of this powerful prayer.',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'red',
    type: 'event'
  },
  {
    id: 4,
    startDate: '2025-07-02',
    endDate: '2025-07-02',
    time: '5:30 AM - 7:00 AM',
    title: 'Morning Meditation & Pranayama',
    location: 'Meditation Garden, Bapuji Dham, Sacred City',
    description: 'Start your day with divine energy through guided meditation and pranayama. Experience inner peace and spiritual awakening in the serene morning atmosphere.',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'purple',
    type: 'event'
  },
  {
    id: 5,
    startDate: '2025-07-05',
    endDate: '2025-07-12',
    title: 'Srimad Bhagavad Gita Katha',
    location: 'Shree Ram Ranuja Dham, Sacred City, India',
    description: 'Deep dive into the eternal wisdom of Bhagavad Gita over 7 days. Bapuji will explain the profound teachings of Lord Krishna and their practical application in modern life.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'blue',
    type: 'katha'
  },
  {
    id: 6,
    startDate: '2025-07-09',
    endDate: '2025-07-09',
    time: '6:00 PM - 8:00 PM',
    title: 'Spiritual Discourse & Bhajan Sandhya',
    location: 'Shree Ram Ranuja Dham, Sacred City, India',
    description: 'An evening of spiritual discourse followed by devotional bhajans. Bapuji will share divine wisdom and guide devotees on the path of righteousness through sacred teachings.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'green',
    type: 'event'
  },
  {
    id: 7,
    startDate: '2025-07-13',
    endDate: '2025-07-13',
    time: '5:00 AM - 10:00 PM',
    title: 'Guru Purnima Celebration',
    location: 'Shree Ram Ranuja Dham, Sacred City, India',
    description: 'Grand celebration of Guru Purnima with special puja, Ram Katha, bhajan sandhya, and prasad distribution. A day dedicated to honoring the divine guru.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'yellow',
    type: 'event'
  },
  {
    id: 8,
    startDate: '2025-07-16',
    endDate: '2025-07-23',
    title: 'Ramayana Katha - Bal Kand',
    location: 'Main Auditorium, Bapuji Dham, Sacred City',
    description: 'Begin the sacred journey through Ramayana with Bal Kand over 7 days. Listen to the divine stories of Lord Rama\'s childhood and early life with spiritual insights.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'orange',
    type: 'katha'
  },
  {
    id: 9,
    startDate: '2025-07-20',
    endDate: '2025-07-20',
    time: '8:00 AM - 6:00 PM',
    title: 'Ekadashi Vrat & Katha',
    location: 'Temple Complex, Sacred City',
    description: 'Observe the sacred Ekadashi fast with proper rituals and listen to the significance of this holy day. Includes special prayers and spiritual guidance.',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'indigo',
    type: 'event'
  },
  {
    id: 10,
    startDate: '2025-07-25',
    endDate: '2025-08-01',
    title: 'Sunderkand Katha',
    location: 'Hanuman Mandir, Bapuji Dham, Sacred City',
    description: 'Sacred 7-day Sunderkand Katha with melodious bhajans dedicated to Hanuman Ji. Experience the divine power and protection of Bajrang Bali through detailed narration.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'red',
    type: 'katha'
  },
  {
    id: 11,
    startDate: '2025-07-27',
    endDate: '2025-07-27',
    time: '9:00 AM - 5:00 PM',
    title: 'Spiritual Retreat & Meditation',
    location: 'Retreat Center, Sacred Hills',
    description: 'A full day spiritual retreat focusing on meditation, self-reflection, and inner transformation. Includes guided meditation sessions and spiritual counseling.',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'purple',
    type: 'event'
  },
  {
    id: 12,
    startDate: '2025-07-30',
    endDate: '2025-07-30',
    time: '7:00 PM - 9:00 PM',
    title: 'Krishna Janmashtami Preparation',
    location: 'Krishna Temple, Bapuji Dham, Sacred City',
    description: 'Special preparation session for Krishna Janmashtami celebration. Learn about the significance of Lord Krishna\'s birth and traditional celebration methods.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'blue',
    type: 'event'
  },
  {
    id: 13,
    startDate: '2025-08-03',
    endDate: '2025-08-03',
    time: '6:00 AM - 11:00 PM',
    title: 'Krishna Janmashtami Celebration',
    location: 'Shree Ram Ranuja Dham, Sacred City, India',
    description: 'Grand celebration of Lord Krishna\'s birth with midnight aarti, bhajan sandhya, dance performances, and prasad distribution. Experience divine joy and bliss.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'blue',
    type: 'event'
  },
  {
    id: 14,
    startDate: '2025-08-05',
    endDate: '2025-08-12',
    title: 'Shrimad Bhagwat Katha',
    location: 'Shree Ram Ranuja Dham, Sacred City, India',
    description: 'Experience the divine stories of Lord Krishna through 7-day Shrimad Bhagwat Katha. Immerse yourself in the eternal leelas and teachings of the Supreme Lord.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'blue',
    type: 'katha'
  },
  {
    id: 15,
    startDate: '2025-08-07',
    endDate: '2025-08-07',
    time: '5:00 PM - 7:00 PM',
    title: 'Vishnu Sahasranama Chanting',
    location: 'Vishnu Temple, Sacred City',
    description: 'Sacred chanting of the thousand names of Lord Vishnu. Experience the divine vibrations and spiritual energy of this powerful prayer.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'green',
    type: 'event'
  },
  {
    id: 16,
    startDate: '2025-08-10',
    endDate: '2025-08-10',
    time: '8:00 AM - 12:00 PM',
    title: 'Yoga & Spiritual Wellness',
    location: 'Wellness Center, Bapuji Dham, Sacred City',
    description: 'Comprehensive yoga session combining asanas, pranayama, and meditation for complete physical and spiritual wellness. Suitable for all levels.',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'purple',
    type: 'event'
  },
  {
    id: 17,
    startDate: '2025-08-15',
    endDate: '2025-08-22',
    title: 'Devi Bhagwat Katha',
    location: 'Devi Temple, Sacred City, India',
    description: 'Sacred 7-day Devi Bhagwat Katha celebrating the divine feminine power. Experience the glory and grace of Maa Durga through ancient scriptures and devotional practices.',
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    color: 'red',
    type: 'katha'
  }
];

// Tailwind CDN v2 has no orange-* utilities — use hex for badge backgrounds
const badgeBgColors = {
  orange: '#f97316',
  green: '#22c55e',
  red: '#ef4444',
  purple: '#a855f7',
  blue: '#3b82f6',
  yellow: '#eab308',
  indigo: '#6366f1'
};

const Events = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'gu' ? 'gu-IN' : 'en-US';

  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage, setEventsPerPage] = useState(6);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showPageSizeDialog, setShowPageSizeDialog] = useState(false);
  const [mobileSearchFocused, setMobileSearchFocused] = useState(false);

  // API-fetched events — replaces the static eventsData array
  const [eventsData, setEventsData] = useState([]);
  const [apiLoading, setApiLoading]  = useState(true);
  const [apiError,   setApiError]    = useState('');

  useEffect(() => {
    setApiLoading(true);
    eventsApi.getAll()
      .then(res => {
        if (res.success) {
          // API returns imageUrl; map to 'image' for backwards compat with existing UI
          setEventsData(res.data.map(e => ({ ...e, image: e.imageUrl || e.image || '' })));
        } else {
          setApiError(t('eventsPage.load_error'));
        }
      })
      .catch(() => setApiError(t('eventsPage.load_error')))
      .finally(() => setApiLoading(false));
  }, [t]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  // Upcoming = still running or future (same rule as Home: end date not before today)
  const filteredEvents = useMemo(() => {
    const searchTerm = search.toLowerCase();

    return eventsData.filter(event => {
      if (!isUpcomingOrLive(event)) return false;

      const matchesSearch = !search ||
        (event.title || '').toLowerCase().includes(searchTerm) ||
        (event.location || '').toLowerCase().includes(searchTerm);

      let matchesDate = true;
      if (dateFilter) {
        const selected = toLocalDate(dateFilter);
        const start = toLocalDate(event.startDate);
        const end = toLocalDate(event.endDate || event.startDate);
        matchesDate = selected && start && end && selected >= start && selected <= end;
      }

      return matchesSearch && matchesDate;
    });
  }, [eventsData, search, dateFilter]);

  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * eventsPerPage;
    return filteredEvents.slice(startIndex, startIndex + eventsPerPage);
  }, [filteredEvents, currentPage, eventsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, dateFilter, eventsPerPage]);

  const clearAllFilters = () => {
    setSearch('');
    setDateFilter('');
  };

  const hasActiveFilters = Boolean(search || dateFilter);

  const formatDateChip = (value) =>
    new Date(value).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric', year: 'numeric' });

  // Group events by Month and Year
  const groupedEvents = useMemo(() => {
    const grouped = {};
    paginatedEvents.forEach(event => {
      const date = new Date(event.startDate);
      const monthYear = `${date.toLocaleDateString(dateLocale, { month: 'long' })} ${date.getFullYear()}`;
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(event);
    });
    return grouped;
  }, [paginatedEvents, dateLocale]);

  const openDirections = (event) => {
    const url = event.mapUrl
      || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location || '')}`;
    window.open(url, '_blank');
  };

  const renderDateBadge = (event, compact = false) => {
    const bg = badgeBgColors[event.color] || badgeBgColors.orange;
    const startDate = new Date(event.startDate);
    const startDay = startDate.toLocaleDateString(dateLocale, { weekday: 'short' });
    const startDayNum = startDate.getDate();
    const startMonth = startDate.toLocaleDateString(dateLocale, { month: 'short' });
    const daySize = compact ? 'text-xl' : 'text-2xl';

    // Badge always shows start date only (full range stays in event details)
    return (
      <div
        className={`text-white rounded-lg ${compact ? 'p-2' : 'p-4'} text-center`}
        style={{ backgroundColor: bg }}
      >
        <div className="text-xs font-semibold uppercase">{startDay}</div>
        <div className={`${daySize} font-bold`}>{startDayNum}</div>
        <div className="text-xs">{startMonth}</div>
      </div>
    );
  };

  const getFormattedDateTimeInfo = (event) => {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    const isMultiDay = event.startDate !== event.endDate;
    const dateOpts = { year: 'numeric', month: 'long', day: 'numeric' };

    if (isMultiDay) {
      return `${startDate.toLocaleDateString(dateLocale, dateOpts)} - ${endDate.toLocaleDateString(dateLocale, dateOpts)}`;
    }
    return `${startDate.toLocaleDateString(dateLocale, dateOpts)}${event.time ? ' @ ' + event.time : ''}`;
  };

  const eventJsonLd = useMemo(() => {
    const upcoming = eventsData.filter(e => isUpcomingOrLive(e) && e.startDate);
    if (!upcoming.length) return null;
    const events = upcoming.slice(0, 10).map(e => ({
      "@type": "Event",
      "name": e.title || "Spiritual Event at Shree Ram Ranuja Dham Ashram",
      "startDate": e.startDate ? `${e.startDate}T00:00:00+05:30` : undefined,
      "endDate": e.endDate ? `${e.endDate}T23:59:59+05:30` : undefined,
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": e.location || "Shree Ram Ranuja Dham Ashram",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Lathi–Bhurkhiya Road, Rampar",
          "addressLocality": "Rampar",
          "addressRegion": "Gujarat",
          "postalCode": "365430",
          "addressCountry": "IN"
        }
      },
      "performer": {
        "@type": "Person",
        "name": "Shree Radhe Krishna Bapu",
        "@id": "https://www.shreeramranujadham.org/#bapu"
      },
      "organizer": {
        "@id": "https://www.shreeramranujadham.org/#organization"
      }
    }));
    return { "@context": "https://schema.org", "@graph": events };
  }, [eventsData]);

  return (
    <>
      <Helmet>
        <title>{t('eventsPage.meta.title')}</title>
        <meta name="description" content={t('eventsPage.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/events" />
        <meta property="og:title" content={t('eventsPage.meta.title')} />
        <meta property="og:description" content={t('eventsPage.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/events" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
        {eventJsonLd && (
          <script type="application/ld+json">{JSON.stringify(eventJsonLd)}</script>
        )}
      </Helmet>

      {/* ── Page Header ─────────────────────────────── */}
      <section
        className="pt-24 pb-8 md:pb-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 50%, #7c3aed 100%)' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-400 bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-yellow-400 bg-opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-4 md:mb-6">
            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 md:px-6 py-2 md:py-3 mb-3 md:mb-4">
              <i className="fas fa-calendar-alt text-yellow-300 mr-2 md:mr-3 text-lg md:text-xl"></i>
              <span className="text-white font-semibold text-sm md:text-base">{t('eventsPage.badge')}</span>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight drop-shadow-lg">
            {t('eventsPage.title_1')}<br />
            <span className="text-yellow-300">{t('eventsPage.title_2')}</span>
          </h1>
        </div>
      </section>

      {/* ── Search & Filter ──────────────────────────── */}
      <section className="pt-4 pb-2 md:py-12 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-10 rounded-full -translate-y-48 translate-x-48 hidden md:block"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-10 rounded-full translate-y-40 -translate-x-40 hidden md:block"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">

            {/* Mobile: compact search + date toolbar */}
            <div className="md:hidden mb-3">
              <p className="text-center text-gray-600 text-sm mb-3">
                {t('eventsPage.count', { count: filteredEvents.length })}
              </p>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-500 text-sm pointer-events-none"></i>
                  <input
                    type="search"
                    inputMode="search"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={t('eventsPage.search_placeholder')}
                    value={
                      search ||
                      (!mobileSearchFocused && dateFilter ? formatDateChip(dateFilter) : '')
                    }
                    onChange={(e) => {
                      if (!search && dateFilter && !mobileSearchFocused) return;
                      setSearch(e.target.value);
                    }}
                    onFocus={() => setMobileSearchFocused(true)}
                    onBlur={() => setMobileSearchFocused(false)}
                    className={`events-mobile-search w-full pl-9 pr-11 py-2.5 bg-white border border-orange-200 rounded-xl focus:outline-none focus:border-orange-500 text-base shadow-sm ${
                      !search && dateFilter && !mobileSearchFocused
                        ? 'text-orange-600 font-medium'
                        : 'text-gray-700'
                    }`}
                    aria-label={t('eventsPage.search_aria')}
                  />
                  {(search || dateFilter) && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      aria-label={t('eventsPage.clear_aria')}
                    >
                      <i className="fas fa-times text-sm"></i>
                    </button>
                  )}
                </div>

                <div className="relative w-11 h-11 flex-shrink-0 rounded-xl border border-gray-300 bg-white shadow-sm overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-gray-700">
                    <i className="fas fa-calendar-alt text-sm"></i>
                    {dateFilter && (
                      <span
                        className="absolute top-1 right-1 w-2 h-2 rounded-full border border-white"
                        style={{ backgroundColor: '#ef4444' }}
                      ></span>
                    )}
                  </div>
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    autoComplete="off"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                    aria-label={t('eventsPage.date_filter_aria')}
                  />
                </div>
              </div>
            </div>

            {/* Desktop: full filter card */}
            <div className="hidden md:block bg-white rounded-2xl p-6 shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 mb-6">
              <div className="flex flex-row gap-4 items-center">
                <div className="flex-1">
                  <div className="relative">
                    <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-orange-500 pointer-events-none"></i>
                    <input
                      type="search"
                      inputMode="search"
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder={t('eventsPage.search_placeholder_desktop')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 text-gray-700"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-sm font-semibold text-gray-600 whitespace-nowrap">{t('eventsPage.date_label')}</label>
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    autoComplete="off"
                    className="px-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 text-gray-700 bg-white"
                  />
                </div>

                <button
                  type="button"
                  onClick={clearAllFilters}
                  disabled={!hasActiveFilters}
                  className={`px-4 py-3 rounded-lg transition-colors font-medium flex items-center justify-center ${
                    hasActiveFilters
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <i className="fas fa-times mr-2"></i>{t('eventsPage.clear')}
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-orange-100">
                <div className="flex flex-row items-center justify-between gap-2">
                  <p className="text-gray-600 text-sm">
                    {t('eventsPage.count', { count: filteredEvents.length })}
                  </p>
                  {hasActiveFilters && (
                    <div className="text-sm text-gray-500">
                      <i className="fas fa-filter mr-1 text-orange-500"></i>
                      <span>
                        {t('eventsPage.showing_results')}
                        {search && t('eventsPage.showing_for', { search })}
                        {dateFilter && t('eventsPage.showing_on', { date: formatDateChip(dateFilter) })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Events List Section ──────────────────────────── */}
      <section className="pb-8 md:pb-12 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Events Container */}
            <div id="eventsContainer">
              {/* API loading / error states */}
              {apiLoading ? (
                <div className="text-center py-16">
                  <i className="fas fa-spinner fa-spin text-orange-500 text-3xl mb-3"></i>
                  <p className="text-gray-500">{t('eventsPage.loading')}</p>
                </div>
              ) : apiError ? (
                <div className="text-center py-16">
                  <i className="fas fa-exclamation-circle text-red-400 text-3xl mb-3"></i>
                  <p className="text-gray-600 font-medium">{apiError}</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="text-center py-8 md:py-16">
                  <div className="hidden md:block text-6xl text-gray-300 mb-4">📅</div>
                  <h3 className="text-lg md:text-2xl font-bold text-gray-600 mb-2">{t('eventsPage.empty_title')}</h3>
                  <p className="text-sm md:text-base text-gray-500">{t('eventsPage.empty_body')}</p>
                </div>
              ) : (
                Object.keys(groupedEvents).map((monthYear) => {
                  const [month, year] = monthYear.split(' ');
                  return (
                    <div key={monthYear} className="mb-8 md:mb-12">
                      <div className="mb-4 md:mb-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                          <span className="text-orange-500">{month}</span> {year}
                        </h2>
                      </div>

                      {/* Event Cards */}
                      {groupedEvents[monthYear].map((event) => {
                        const dateText = getFormattedDateTimeInfo(event);

                        if (isMobile) {
                          // Mobile Layout
                          return (
                            <div
                              key={event.id}
                              className="bg-white rounded-2xl p-4 shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 mb-4"
                            >
                              <div className="flex gap-3">
                                <div className="w-24 h-16 flex-shrink-0">
                                  <img
                                    src={event.image}
                                    alt={event.title}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs text-orange-600 font-medium mb-1">
                                    {dateText}
                                  </div>
                                  <h3 className="text-base font-bold text-gray-800 mb-2 leading-tight">
                                    {event.title}
                                  </h3>
                                </div>
                              </div>

                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center text-xs text-gray-600">
                                    <i className="fas fa-map-marker-alt mr-1.5 text-orange-500"></i>
                                    <span>{event.location}</span>
                                  </div>
                                  <button
                                    onClick={() => openDirections(event)}
                                    className="flex items-center text-xs font-medium px-2 py-1 rounded-lg flex-shrink-0 transition-colors"
                                    style={{ color: '#2563eb', background: '#eff6ff' }}
                                  >
                                    <i className="fas fa-directions mr-1"></i>{t('eventsPage.directions')}
                                  </button>
                                </div>
                                <p className="text-gray-700 text-sm leading-relaxed">
                                  {event.description}
                                </p>
                              </div>
                            </div>
                          );
                        } else {
                          // Desktop Layout
                          return (
                            <div
                              key={event.id}
                              className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 mb-6"
                            >
                              <div className="flex gap-6">
                                <div className="w-24 flex-shrink-0">
                                  {renderDateBadge(event)}
                                </div>
                                <div className="w-48 flex-shrink-0">
                                  <img
                                    src={event.image}
                                    alt={event.title}
                                    className="w-full h-32 object-cover rounded-lg"
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm text-orange-600 font-medium mb-2">
                                    {dateText}
                                  </div>
                                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                                    {event.title}
                                  </h3>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center text-sm text-gray-600">
                                      <i className="fas fa-map-marker-alt mr-2 text-orange-500"></i>
                                      {event.location}
                                    </div>
                                    <button
                                      onClick={() => openDirections(event)}
                                      className="flex items-center text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
                                      style={{ color: '#2563eb', background: '#eff6ff' }}
                                    >
                                      <i className="fas fa-directions mr-1"></i>{t('eventsPage.directions')}
                                    </button>
                                  </div>
                                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                                    {event.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="py-6 md:py-8 border-t border-orange-100 mt-8">
                {isMobile ? (
                  // Mobile Pagination
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-xs text-gray-500">{t('eventsPage.per_page')}</span>
                      <button
                        type="button"
                        onClick={() => setShowPageSizeDialog(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors"
                      >
                        {eventsPerPage}
                        <i className="fas fa-chevron-down text-xs text-gray-400"></i>
                      </button>
                    </div>
                    <div className="flex items-center justify-between w-full">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors text-sm ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <i className="fas fa-chevron-left mr-1"></i>{t('eventsPage.previous')}
                      </button>
                      <span className="text-gray-600 text-sm font-medium">{t('eventsPage.page_of', { current: currentPage, total: totalPages })}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors text-sm ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        {t('eventsPage.next')}<i className="fas fa-chevron-right ml-1"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  // Desktop Pagination
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <i className="fas fa-chevron-left mr-2"></i>{t('eventsPage.previous_events')}
                    </button>

                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 text-sm font-medium">{t('eventsPage.page_of', { current: currentPage, total: totalPages })}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{t('eventsPage.per_page')}</span>
                        <select
                          value={eventsPerPage}
                          onChange={(e) => setEventsPerPage(parseInt(e.target.value, 10))}
                          className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-orange-500 bg-white"
                        >
                          {EVENT_PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`flex items-center text-gray-600 hover:text-orange-600 font-medium px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      {t('eventsPage.next_events')}<i className="fas fa-chevron-right mr-2"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <PageSizeDialog
        open={showPageSizeDialog}
        onClose={() => setShowPageSizeDialog(false)}
        value={eventsPerPage}
        onChange={setEventsPerPage}
        options={EVENT_PAGE_SIZE_OPTIONS}
        title={t('eventsPage.page_size_title')}
        optionSuffix={t('eventsPage.page_size_suffix')}
      />
    </>
  );
};

export default Events;
